import { useEffect, useRef, useState } from "react";
import { api, streamJob } from "../api/client";
import { Icon } from "./icons";

interface Props {
  jobId: string;
  title?: string;
  onDone?: (status: string) => void;
}

// Plain-English labels for the engine's internal progress stages, so the panel
// reads as a status — not a terminal. Unknown stages fall back to "Working…".
const STAGE_LABEL: Record<string, string> = {
  extract: "Reading the manuscript",
  transcribe: "Transcribing the audio",
  align: "Comparing the audio to the script",
  match: "Matching to the manuscript",
  propose: "Mapping chapters",
  ai_resolve: "Reviewing tricky chapters",
  confirm: "Building chapters",
  proof: "Proofing",
  analyze: "Analysing meaning impact",
  spec_check: "Checking delivery specs",
  skip: "Skipping finished chapters",
  done: "Finishing up",
};
function friendlyStage(s: string | null): string {
  if (!s) return "Starting";
  return STAGE_LABEL[s] || "Working";
}

function lineClass(line: string): string {
  const l = line.toLowerCase();
  if (l.startsWith("[error]") || l.startsWith("[fail]") || l.includes("traceback")) return "l-fail";
  if (l.startsWith("[skip]")) return "l-skip";
  if (l.startsWith("[done]")) return "l-done";
  if (/^\[(stage|extract|transcribe|align|match|propose|proof|chapter)/.test(l)) return "l-stage";
  return "";
}

export function RunPanel({ jobId, title, onDone }: Props) {
  const [lines, setLines] = useState<string[]>([]);
  const [pct, setPct] = useState(0);
  const [stage, setStage] = useState<string | null>(null);
  const [steps, setSteps] = useState<string[]>([]); // main stages, in first-seen order
  const [status, setStatus] = useState("running");
  const [showLog, setShowLog] = useState(false);
  const logRef = useRef<HTMLDivElement>(null);
  const stick = useRef(true);

  useEffect(() => {
    setLines([]);
    setPct(0);
    setStage(null);
    setSteps([]);
    setStatus("running");
    const close = streamJob(jobId, {
      onLog: (line) => setLines((cur) => [...cur, line]),
      onProgress: (p, s) => {
        setPct(p);
        setStage(s);
        // Remember the main pipeline stages in the order they first appear, so
        // the panel can show what's been done. Skip the per-chapter/finish noise.
        if (s && s in STAGE_LABEL && s !== "done" && s !== "skip") {
          setSteps((cur) => (cur.includes(s) ? cur : [...cur, s]));
        }
      },
      onMeta: (m) => setStatus(m.status),
      onEnd: (m) => {
        setStatus(m.status);
        setPct((p) => (m.status === "completed" ? 100 : p));
        onDone?.(m.status);
      },
    });
    return close;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  useEffect(() => {
    if (showLog && stick.current && logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [lines, showLog]);

  async function cancel() {
    try { await api(`/api/jobs/${jobId}/cancel`, { method: "POST" }); } catch { /* ignore */ }
  }

  const running = status === "running" || status === "queued";
  const completed = status === "completed";
  // While running, the CURRENT stage is shown in the headline above, so the
  // checklist lists only the COMPLETED stages — otherwise the active stage
  // appears twice (headline + active step). On finish, show every step done.
  const doneSteps = running ? steps.filter((s) => s !== stage) : steps;

  return (
    <div className="card run-panel">
      <div className="run-head">
        <strong style={{ fontSize: 13 }}>{title || "Run"}</strong>
        <div className="run-bar-track">
          <div className={`run-bar-fill${running || completed ? "" : " bad"}`} style={{ width: `${pct}%` }} />
        </div>
        <span className="muted" style={{ fontSize: 12, minWidth: 40, textAlign: "right" }}>
          {Math.round(pct)}%
        </span>
        {running ? (
          <button className="btn danger sm" onClick={cancel}>Cancel</button>
        ) : (
          <span className={`pill ${completed ? "ok" : "bad"}`}>{completed ? "done" : status}</span>
        )}
      </div>

      {/* Friendly status — a plain one-liner with a spinner, instead of a log. */}
      <div className="run-status">
        {running ? (
          <><span className="spinner" /><span className="run-stage-now">
            {/* Queued: for the worker to pick it up, or behind this project's job of the same kind. */}
            {status === "queued" ? "Waiting its turn" : friendlyStage(stage)}…
          </span></>
        ) : completed ? (
          <><span className="run-ok"><Icon name="check" size={18} /></span><span className="run-stage-now">All done</span></>
        ) : (
          <><span className="run-fail"><Icon name="info" size={17} /></span><span className="run-stage-now">Stopped before finishing</span></>
        )}
      </div>

      {doneSteps.length > 0 && (
        <div className="run-steps">
          {doneSteps.map((s) => (
            <span key={s} className="run-step done">
              <Icon name="check" size={12} className="check" />
              {friendlyStage(s)}
            </span>
          ))}
        </div>
      )}

      <button className="run-details-toggle" onClick={() => setShowLog((v) => !v)}>
        <Icon name={showLog ? "chevron-up" : "chevron-down"} size={13} />
        {showLog ? "Hide technical details" : "Show technical details"}
      </button>

      {showLog && (
        <div
          className="run-log"
          ref={logRef}
          onScroll={(e) => {
            const el = e.currentTarget;
            stick.current = el.scrollHeight - el.clientHeight - el.scrollTop < 40;
          }}
        >
          {lines.length === 0 ? (
            <span className="l-skip">Starting…</span>
          ) : (
            lines.map((l, i) => <div key={i} className={lineClass(l)}>{l}</div>)
          )}
        </div>
      )}
    </div>
  );
}
